import { useT } from "@/i18n/I18nProvider";
import { PageHero } from "./PageHero";
import { PendingContentBlock } from "./PendingContentBlock";
import { SEO } from "./SEO";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { RESOURCE_CONTENT } from "@/data/resourcesContent";
import type { RouteKey } from "@/lib/routes";

interface DetailPageProps {
  routeKey: RouteKey;
  imageUrl?: string;
  imageAlt?: string;
  related?: RouteKey[];
}

/**
 * Shared template for the Resources detail pages. Page-specific copy comes
 * from the resourcesContent.ts registry when present; otherwise the shared
 * structural placeholder blocks are shown (never invented copy).
 */
export function DetailPage({ routeKey, imageUrl, imageAlt, related = [] }: DetailPageProps) {
  const { t, path, locale } = useT();
  const content = RESOURCE_CONTENT[routeKey]?.[locale];

  return (
    <>
      <SEO routeKey={routeKey} />
      <PageHero routeKey={routeKey} imageUrl={imageUrl} imageAlt={imageAlt} />

      <section className="container-bnm py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <p className="eyebrow mb-4">{t.sections.scopeLabel}</p>
            <p className="text-slate2 max-w-prose-tight leading-relaxed">
              {(t.routes[routeKey] as { lede?: string }).lede ?? t.meta.tagline}
            </p>
          </aside>

          <div className="lg:col-span-8 flex flex-col gap-12">
            {content ? (
              <div className="flex flex-col gap-10" data-testid={`resource-content-${routeKey}`}>
                {content.note && (
                  <div className="border-l-2 border-clay pl-6 py-3" role="note">
                    <p className="text-sm text-slate2 leading-relaxed max-w-prose">
                      {content.note}
                    </p>
                  </div>
                )}
                {content.sections.map((section) => (
                  <div key={section.heading}>
                    <p className="eyebrow mb-4">{section.heading}</p>
                    {section.paragraphs && (
                      <div className="flex flex-col gap-4 max-w-prose">
                        {section.paragraphs.map((p, idx) => (
                          <p key={idx} className="text-slate2 leading-relaxed text-[15px]">
                            {p}
                          </p>
                        ))}
                      </div>
                    )}
                    {section.items && (
                      <ul className="flex flex-col border-t border-bone-500">
                        {section.items.map((item, idx) => (
                          <li
                            key={idx}
                            className="grid grid-cols-12 gap-4 md:gap-8 border-b border-bone-500 py-4"
                          >
                            <span className="col-span-1 font-mono text-[11px] text-clay pt-0.5" aria-hidden>
                              —
                            </span>
                            <p className="col-span-11 text-slate2 leading-relaxed text-[15px]">
                              {item}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <PendingContentBlock label={t.sections.overviewLabel} testId={`pending-overview-${routeKey}`} />
                <PendingContentBlock label={t.sections.counterpartiesLabel} testId={`pending-counterparties-${routeKey}`} />
                <PendingContentBlock label={t.sections.complianceLabel} testId={`pending-compliance-${routeKey}`} />
              </>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-bone-500 bg-bone-300/40">
          <div className="container-bnm py-16 md:py-20">
            <p className="eyebrow mb-8">{t.sections.seeAlso}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((k) => (
                <Link
                  key={k}
                  to={path(k)}
                  className="group border border-bone-500 bg-bone p-6 md:p-8 hover:border-ink transition-colors"
                  data-testid={`related-${k.replace(".", "-")}`}
                >
                  <p className="eyebrow mb-4">
                    {(t.routes[k] as { eyebrow?: string }).eyebrow}
                  </p>
                  <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-clay transition-colors">
                    {t.routes[k].label}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-slate2 group-hover:text-clay transition-colors">
                    <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactCta />
    </>
  );
}

export function ContactCta() {
  const { t, path } = useT();
  return (
    <section className="bg-ink text-bone" data-testid="contact-cta">
      <div className="container-bnm py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
        <div className="lg:col-span-8">
          <p className="eyebrow !text-bone/60 mb-6">{t.home.ctaEyebrow}</p>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-bone">
            {t.home.ctaTitle}
          </h2>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link
            to={path("contact")}
            className="inline-flex items-center gap-3 bg-clay text-bone px-8 py-4 font-medium hover:bg-clay-600 transition-colors group"
            data-testid="cta-contact"
          >
            {t.home.ctaAction}
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
